import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, X } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';

const MaintenanceBanner = () => {
  const [maintenance, setMaintenance] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [globalMessages, setGlobalMessages] = useState([]);

  useEffect(() => {
    checkMaintenanceStatus();
    fetchGlobalMessages();
    
    // Check every 30 seconds
    const interval = setInterval(() => {
      checkMaintenanceStatus();
      fetchGlobalMessages();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (maintenance?.warning_countdown_end) {
      const countdownInterval = setInterval(() => {
        const end = new Date(maintenance.warning_countdown_end);
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0) {
          setCountdown(null);
          clearInterval(countdownInterval);
          // Refresh maintenance status
          checkMaintenanceStatus();
        } else {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      }, 1000);
      
      return () => clearInterval(countdownInterval);
    }
  }, [maintenance?.warning_countdown_end]);

  const checkMaintenanceStatus = async () => {
    try {
      const response = await api.get('/maintenance/status');
      setMaintenance(response.data);
    } catch (error) {
      console.error('Failed to check maintenance status:', error);
    }
  };

  const fetchGlobalMessages = async () => {
    try {
      const response = await api.get('/global-messages/active');
      setGlobalMessages(response.data.messages || []);
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  };

  // Don't show if dismissed or no warning
  if (dismissed) return null;
  
  const showWarning = maintenance?.warning_active || maintenance?.is_scheduled;
  const hasMessages = globalMessages.length > 0;
  
  if (!showWarning && !hasMessages) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Maintenance Warning Banner */}
      {showWarning && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 shadow-lg" data-testid="maintenance-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 animate-pulse" />
              <div>
                <span className="font-semibold">
                  {maintenance?.warning_message || 'Scheduled maintenance approaching'}
                </span>
                {maintenance?.scheduled_at && (
                  <span className="ml-2 text-amber-100">
                    Scheduled: {new Date(maintenance.scheduled_at).toLocaleString()}
                  </span>
                )}
                {maintenance?.eta_message && (
                  <span className="ml-2 text-amber-100">
                    • {maintenance.eta_message}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {countdown && (
                <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                  <Clock className="h-4 w-4" />
                  <span className="font-mono font-bold">{countdown}</span>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={() => setDismissed(true)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Global Messages */}
      {globalMessages.map((msg) => (
        <div
          key={msg.id}
          className={`px-4 py-2 text-white shadow ${
            msg.message_type === 'warning' ? 'bg-amber-600' :
            msg.message_type === 'urgent' ? 'bg-red-600' :
            msg.message_type === 'maintenance' ? 'bg-orange-500' :
            'bg-blue-600'
          }`}
          data-testid={`global-message-${msg.id}`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <span className="font-semibold">{msg.title}:</span>
              <span className="ml-2">{msg.message}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MaintenanceBanner;
